import { comparePairingCriteria, matchByCriteria } from './pairingCriteria';

function buildTeamBoardPairingStats({
  players = [],
  playerStandings = [],
  playerHistory = new Map(),
  controlType = null,
} = {}) {
  const statsByPlayerId = new Map();
  const standingsByPlayerId = new Map(
    Array.isArray(playerStandings) ? playerStandings.map((row) => [row.id, row]) : []
  );

  const ensureStats = (player) => {
    if (!player?.id) return null;
    const standing = standingsByPlayerId.get(player.id);
    const history = playerHistory?.get?.(player.id) ?? {};

    if (!statsByPlayerId.has(player.id)) {
      const ratingEntry = controlType ? player?.ratingsMap?.[controlType] : null;
      statsByPlayerId.set(player.id, {
        id: player.id,
        points: Number(standing?.points) || 0,
        coefficient: Number(standing?.coefficient) || 0,
        rating: typeof standing?.startRating === 'number'
          ? standing.startRating
          : ((ratingEntry && typeof ratingEntry.rating === 'number') ? ratingEntry.rating : 0),
        whiteCount: Number(history?.whiteCount) || 0,
        blackCount: Number(history?.blackCount) || 0,
        byeCount: Number(history?.byeCount) || 0,
        lastColors: Array.isArray(history?.lastColors) ? [...history.lastColors] : [],
      });
    }

    return statsByPlayerId.get(player.id);
  };

  players.forEach((player) => ensureStats(player));

  return statsByPlayerId;
}

function getTeamBoardPairingPlayers(team, {
  players = [],
  teamSize = null,
  statsByPlayerId = new Map(),
  avoidRepeatByes = false,
} = {}) {
  const playerById = new Map(players.map((player) => [player.id, player]));
  const members = Array.isArray(team?.players) ? team.players : [];

  return members
    .map((member) => playerById.get(member?.id) ?? member)
    .filter((player) => player?.id)
    .sort((a, b) => {
      const aStats = statsByPlayerId.get(a.id);
      const bStats = statsByPlayerId.get(b.id);
      if (avoidRepeatByes) {
        const byByeCount = (bStats?.byeCount || 0) - (aStats?.byeCount || 0);
        if (byByeCount) return byByeCount;
      }

      const byPoints = (bStats?.points || 0) - (aStats?.points || 0);
      if (byPoints) return byPoints;

      const byCoefficient = (bStats?.coefficient || 0) - (aStats?.coefficient || 0);
      if (byCoefficient) return byCoefficient;

      const byRating = (bStats?.rating || 0) - (aStats?.rating || 0);
      if (byRating) return byRating;

      return String(a.id).localeCompare(String(b.id));
    })
    .slice(0, teamSize || undefined);
}

const SAME_REPEAT_PAIR_COLOR_PENALTY = 100;

function getPlayerIds(game) {
  return {
    whitePlayerId: game?.getSingleWhitePlayerId?.() ?? game?.whitePlayerIds?.[0] ?? null,
    blackPlayerId: game?.getSingleBlackPlayerId?.() ?? game?.blackPlayerIds?.[0] ?? null,
  };
}

function findLastGameBetweenPlayers(player1Id, player2Id, pastGames = []) {
  if (!player1Id || !player2Id) return null;

  return pastGames
    .filter((game) => {
      const { whitePlayerId, blackPlayerId } = getPlayerIds(game);
      return (
        (whitePlayerId === player1Id && blackPlayerId === player2Id) ||
        (whitePlayerId === player2Id && blackPlayerId === player1Id)
      );
    })
    .sort((a, b) =>
      (b.round || 0) - (a.round || 0) ||
      (b.cycle || 0) - (a.cycle || 0) ||
      (b.board || 0) - (a.board || 0) ||
      (b.number || 0) - (a.number || 0)
    )[0] ?? null;
}

function getRepeatPairColorPenalty({
  side1Player,
  side2Player,
  currentWhitePlayer,
  currentBlackPlayer,
  pastGames,
}) {
  const lastGame = findLastGameBetweenPlayers(side1Player?.id, side2Player?.id, pastGames);
  if (!lastGame || !currentWhitePlayer?.id || !currentBlackPlayer?.id) return 0;

  const { whitePlayerId, blackPlayerId } = getPlayerIds(lastGame);
  const repeatsSameColors =
    whitePlayerId === currentWhitePlayer.id &&
    blackPlayerId === currentBlackPlayer.id;

  return repeatsSameColors ? SAME_REPEAT_PAIR_COLOR_PENALTY : 0;
}

function scoreTeamBoardColorPattern(
  assignments,
  side1WhiteOnFirstBoard,
  statsByPlayerId,
  {
    pastGames = [],
    mirrorRepeatPairColors = false,
    strictColors = false,
  } = {}
) {
  return assignments.reduce((score, assignment, index) => {
    const side1White = index % 2 === 0 ? side1WhiteOnFirstBoard : !side1WhiteOnFirstBoard;
    const entries = [
      { player: assignment.side1Player, color: side1White ? 'white' : 'black' },
      { player: assignment.side2Player, color: side1White ? 'black' : 'white' },
    ];
    const currentWhitePlayer = side1White ? assignment.side1Player : assignment.side2Player;
    const currentBlackPlayer = side1White ? assignment.side2Player : assignment.side1Player;
    const repeatPairColorPenalty = mirrorRepeatPairColors
      ? getRepeatPairColorPenalty({
          side1Player: assignment.side1Player,
          side2Player: assignment.side2Player,
          currentWhitePlayer,
          currentBlackPlayer,
          pastGames,
        })
      : 0;

    return score + repeatPairColorPenalty + entries.reduce((entryScore, entry) => {
      if (!entry.player?.id) return entryScore;

      const stats = statsByPlayerId.get(entry.player.id);
      const whiteCount = stats?.whiteCount || 0;
      const blackCount = stats?.blackCount || 0;
      const nextWhiteCount = whiteCount + (entry.color === 'white' ? 1 : 0);
      const nextBlackCount = blackCount + (entry.color === 'black' ? 1 : 0);
      const lastColors = stats?.lastColors || [];
      const lastColor = lastColors[lastColors.length - 1] ?? null;
      let sameColorStreak = 0;

      for (let i = lastColors.length - 1; i >= 0 && lastColors[i] === entry.color; i--) {
        sameColorStreak += 1;
      }

      let penalty = Math.abs(nextWhiteCount - nextBlackCount) * 10;
      if (strictColors) {
        if (Math.abs(nextWhiteCount - nextBlackCount) > 2 || sameColorStreak >= 2) return entryScore + 1_000_000;
        const difference = whiteCount - blackCount;
        if ((difference > 0 && entry.color === 'white') || (difference < 0 && entry.color === 'black')) return entryScore + 10_000;
        return entryScore + (lastColor === entry.color ? 100 : 0);
      }
      if (lastColor === entry.color) penalty += 25;
      if (sameColorStreak > 1) penalty += sameColorStreak * 25;

      return entryScore + penalty;
    }, 0);
  }, 0);
}

// Перестановки внутри одного цвета эквивалентны: соперников затем выбирает
// паросочетание. Перебираем только распределение игроков по чётным/нечётным доскам.
function alternatingBoardOrders(players) {
  const orders = [players];
  const count = Math.ceil(players.length / 2);
  const visit = (start, selected) => {
    if (orders.length >= 128) return;
    if (selected.length === count) {
      const chosen = new Set(selected);
      const even = players.filter((_, index) => chosen.has(index));
      const odd = players.filter((_, index) => !chosen.has(index));
      orders.push(players.map((_, index) => index % 2 === 0 ? even[index / 2] : odd[(index - 1) / 2]));
      return;
    }
    for (let index = start; index <= players.length - (count - selected.length); index++) {
      visit(index + 1, [...selected, index]);
    }
  };
  visit(0, []);
  return orders;
}

/** План конкретных досок случайных команд; используется и при оценке, и при создании партий. */
export function planRandomTeamBoards({ side1Team, side2Team, playerStandings, playerHistory, controlType, opponentCounts }) {
  const players = [...side1Team.players, ...side2Team.players];
  const stats = buildTeamBoardPairingStats({ players, playerStandings, playerHistory, controlType });
  const first = getTeamBoardPairingPlayers(side1Team, { players, statsByPlayerId: stats });
  const second = getTeamBoardPairingPlayers(side2Team, { players, statsByPlayerId: stats });
  const firstIds = new Set(first.map(player => player.id));
  const byId = new Map(players.map(player => [player.id, player]));
  let best = null;
  for (const order of alternatingBoardOrders(first)) {
    for (const whiteFirst of [true, false]) {
      const evaluate = (a, b) => {
        const side1Player = firstIds.has(a.id) ? a : b;
        const side2Player = firstIds.has(a.id) ? b : a;
        const white = order.indexOf(side1Player) % 2 === 0 ? whiteFirst : !whiteFirst;
        const repeats = opponentCounts.get([a.id, b.id].sort().join('|')) || 0;
        return [repeats ** 2,
          scoreTeamBoardColorPattern([{ side1Player, side2Player }], white, stats, { strictColors: true }),
          Math.round(Math.abs(stats.get(a.id).points - stats.get(b.id).points) * 1000)];
      };
      const pairs = matchByCriteria(players, evaluate, (a, b) => firstIds.has(a.id) !== firstIds.has(b.id));
      if (pairs.length !== first.length || first.length !== second.length) throw new Error('Incomplete random team board pairing');
      const assignments = pairs.map(([a, b]) => {
        const side1Player = byId.get(firstIds.has(a) ? a : b);
        const side2Player = byId.get(firstIds.has(a) ? b : a);
        const white = order.indexOf(side1Player) % 2 === 0 ? whiteFirst : !whiteFirst;
        return { side1Player, side2Player,
          whitePlayer: white ? side1Player : side2Player, blackPlayer: white ? side2Player : side1Player,
          whiteTeam: white ? side1Team : side2Team, blackTeam: white ? side2Team : side1Team };
      }).sort((a, b) => order.indexOf(a.side1Player) - order.indexOf(b.side1Player));
      const criteria = assignments.reduce((total, item) => {
        const values = evaluate(item.side1Player, item.side2Player);
        return total.map((value, index) => value + values[index]);
      }, [0, 0, 0]);
      if (!best || comparePairingCriteria(criteria, best.criteria) < 0) best = { assignments, criteria };
    }
  }
  return best;
}

export function planRandomTeamCommonColors({ side1Team, side2Team, playerStandings, playerHistory, controlType }) {
  const stats = buildTeamBoardPairingStats({ players: [...side1Team.players, ...side2Team.players], playerStandings, playerHistory, controlType });
  const score = whiteFirst => side1Team.players.reduce((sum, player) => sum +
    scoreTeamBoardColorPattern([{ side1Player: player }], whiteFirst, stats, { strictColors: true }), 0)
    + side2Team.players.reduce((sum, player) => sum +
      scoreTeamBoardColorPattern([{ side2Player: player }], whiteFirst, stats, { strictColors: true }), 0);
  const white = score(true);
  const black = score(false);
  return { swap: black < white, penalty: Math.min(white, black) };
}

export function buildTeamBoardPairingAssignments({
  side1Team,
  side2Team,
  players = [],
  teamSize = null,
  controlType = null,
  playerStandings = [],
  playerHistory = new Map(),
  pastGames = [],
  mirrorRepeatPairColors = false,
  avoidRepeatByes = false,
} = {}) {
  const statsByPlayerId = buildTeamBoardPairingStats({
    players,
    playerStandings,
    playerHistory,
    controlType,
  });
  const side1Players = getTeamBoardPairingPlayers(side1Team, {
    players,
    teamSize,
    statsByPlayerId,
    avoidRepeatByes,
  });
  const side2Players = getTeamBoardPairingPlayers(side2Team, {
    players,
    teamSize,
    statsByPlayerId,
    avoidRepeatByes,
  });
  const availableBoardsCount = Math.max(side1Players.length, side2Players.length);
  const boardsCount = teamSize
    ? Math.min(availableBoardsCount, teamSize)
    : availableBoardsCount;
  const boardPairs = [];

  for (let index = 0; index < boardsCount; index++) {
    const side1Player = side1Players[index] ?? null;
    const side2Player = side2Players[index] ?? null;
    if (side1Player || side2Player) {
      boardPairs.push({ side1Player, side2Player });
    }
  }

  const colorScoringOptions = { pastGames, mirrorRepeatPairColors };
  const side1WhiteScore = scoreTeamBoardColorPattern(
    boardPairs,
    true,
    statsByPlayerId,
    colorScoringOptions
  );
  const side1BlackScore = scoreTeamBoardColorPattern(
    boardPairs,
    false,
    statsByPlayerId,
    colorScoringOptions
  );
  const side1WhiteOnFirstBoard = side1WhiteScore <= side1BlackScore;

  return boardPairs.map((boardPair, index) => {
    const side1White = index % 2 === 0 ? side1WhiteOnFirstBoard : !side1WhiteOnFirstBoard;

    return {
      ...boardPair,
      whitePlayer: side1White ? boardPair.side1Player : boardPair.side2Player,
      blackPlayer: side1White ? boardPair.side2Player : boardPair.side1Player,
      whiteTeam: side1White ? side1Team : side2Team,
      blackTeam: side1White ? side2Team : side1Team,
    };
  });
}
